import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api, { inr } from "@/lib/api";
import { Check, Sparkles, Shield, Clock, PhoneCall, Wallet, CreditCard, FileText, CheckCircle2, Eye, EyeOff, UserPlus, UserCheck, Lock } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import steamRoomImg from "@/assets/steam_room_hammam.webp";
import drinksImg from "@/assets/drinks_hammam.webp";
import newCommonShowerImg from "@/assets/new_common_shower.png";
import newDarkRoomImg from "@/assets/new_dark_room.png";
import newDrinksBarImg from "@/assets/new_driniks_bar.png";
import newHugeJacuzziImg from "@/assets/new_huge_jacuzzi.png";

const getPlanTheme = (planName, isPopular) => {
  const name = (planName || "").toLowerCase();
  if (name.includes("silver")) {
    return {
      border: "border border-cyan-400/80 shadow-lg shadow-cyan-950/30",
      title: "text-transparent bg-clip-text bg-gradient-to-r from-cyan-300 via-teal-200 to-white",
      eyebrow: "text-cyan-400 font-bold",
      price: "text-cyan-400",
      wallet: "text-cyan-300",
      savePill: "text-cyan-400 border border-cyan-500/40 bg-cyan-950/30",
      check: "text-cyan-400",
      btn: "bg-gradient-to-r from-cyan-600 to-teal-600 hover:from-cyan-500 hover:to-teal-500 text-white font-extrabold rounded-xl py-3.5 text-xs uppercase tracking-wider shadow-lg shadow-cyan-950/40 hover:scale-105 transition-all",
    };
  }
  if (name.includes("gold") || isPopular) {
    return {
      border: "border-2 border-amber-400 shadow-xl shadow-amber-950/40 scale-[1.03]",
      title: "text-transparent bg-clip-text bg-gradient-to-r from-amber-300 via-hgold to-yellow-400",
      eyebrow: "text-amber-400 font-bold",
      price: "text-amber-400",
      wallet: "text-hgold",
      savePill: "text-amber-300 border border-amber-500/40 bg-amber-950/30",
      check: "text-amber-400",
      btn: "bg-gradient-to-r from-amber-500 via-hgold to-yellow-500 hover:from-amber-400 hover:to-yellow-400 text-black font-extrabold rounded-xl py-3.5 text-xs uppercase tracking-wider shadow-lg shadow-amber-950/40 hover:scale-105 transition-all",
    };
  }
  if (name.includes("platinum")) {
    return {
      border: "border border-emerald-400/80 shadow-lg shadow-emerald-950/30",
      title: "text-transparent bg-clip-text bg-gradient-to-r from-emerald-300 via-teal-200 to-white",
      eyebrow: "text-emerald-400 font-bold",
      price: "text-emerald-400",
      wallet: "text-emerald-300",
      savePill: "text-emerald-400 border border-emerald-500/40 bg-emerald-950/30",
      check: "text-emerald-400",
      btn: "bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-extrabold rounded-xl py-3.5 text-xs uppercase tracking-wider shadow-lg shadow-emerald-950/40 hover:scale-105 transition-all",
    };
  }
  return {
    border: "border border-purple-500/80 shadow-lg shadow-purple-950/30",
    title: "text-transparent bg-clip-text bg-gradient-to-r from-purple-300 via-pink-200 to-white",
    eyebrow: "text-purple-400 font-bold",
    price: "text-purple-400",
    wallet: "text-purple-300",
    savePill: "text-purple-400 border border-purple-500/40 bg-purple-950/30",
    check: "text-purple-400",
    btn: "bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-extrabold rounded-xl py-3.5 text-xs uppercase tracking-wider shadow-lg shadow-purple-950/40 hover:scale-105 transition-all",
  };
};

export default function Membership() {
  const { user, register } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  // Lead modal states
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [leadForm, setLeadForm] = useState({ name: "", phone: "", email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    document.title = "Membership Plans & Bonus Wallet Credits | Hammam Mens Spa";
    api.get("/memberships")
      .then((r) => {
        setPlans(r.data);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }, []);

  const handleApply = async (e) => {
    e.preventDefault();
    if (!leadForm.name || !leadForm.phone) {
      toast.error("Please enter Name and Phone Number");
      return;
    }

    setSubmitting(true);
    try {
      // If user is not logged in, create account first!
      if (!user) {
        if (!leadForm.email || !leadForm.password) {
          toast.error("Please enter Email Address and Password to create your account.");
          setSubmitting(false);
          return;
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(leadForm.email)) {
          toast.error("Please enter a valid email address.");
          setSubmitting(false);
          return;
        }
        if (leadForm.password.length < 6) {
          toast.error("Password must be at least 6 characters long.");
          setSubmitting(false);
          return;
        }

        const regRes = await register({
          name: leadForm.name,
          email: leadForm.email,
          phone: leadForm.phone,
          password: leadForm.password
        });

        if (!regRes.ok) {
          toast.error(regRes.error || "Failed to create account. Email or Phone may already be registered.");
          setSubmitting(false);
          return;
        }
      }

      // Now post membership request
      try {
        await api.post("/memberships/request", {
          name: leadForm.name,
          phone: leadForm.phone,
          email: leadForm.email || "",
          plan_id: selectedPlan?.id || "",
          plan_name: selectedPlan?.name || "",
          price: selectedPlan?.price || 0,
          wallet_value: selectedPlan?.wallet_value || 0,
          notes: `Requested via Membership Page`
        });
      } catch (err) {
        if (err?.response?.status === 404) {
          // Fallback for live server if backend FastAPI has not been restarted with new route
          await api.post("/memberships/purchase", {
            plan_id: selectedPlan?.id || ""
          });
        } else {
          throw err;
        }
      }
      
      toast.success(
        !user
          ? "🎉 Account created & Membership request submitted! Admin will credit your wallet upon approval."
          : "Membership request submitted! Admin will credit your wallet upon approval."
      );
      setShowModal(false);
      setShowForm(false);
      setLeadForm({ name: "", phone: "", email: "", password: "" });
    } catch {
      toast.error("Failed to submit request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center">
        <div className="premium-spinner mb-4"></div>
        <div className="label-eyebrow text-white/60">Loading Membership Plans...</div>
      </div>
    );
  }

  return (
    <div data-testid="membership-page">
      {/* Hero Header */}
      <section className="relative min-h-[50vh] overflow-hidden -mt-[72px] flex items-center">
        <img src="/hero_image_hammam.png" alt="Membership" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/85 to-black/45" />
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.0, ease: [0.25, 1, 0.5, 1] }}
          className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-40 pb-20 w-full"
        >
          <div className="label-eyebrow mb-6 text-emerald-400 font-extrabold tracking-widest uppercase">Elite Spa Club Privilege</div>
          <h1 className="text-5xl lg:text-7xl font-heading text-transparent bg-clip-text bg-gradient-to-r from-white via-amber-200 to-emerald-400 mb-6">
            Hammam Memberships
          </h1>
          <p className="text-hsecondary text-lg max-w-2xl mt-4 leading-relaxed font-normal">
            Unlock premium male-to-male massage therapies and exclusive spa privileges. Pay less, visit more, and enjoy luxury benefits across all branches.
          </p>
        </motion.div>
      </section>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">

      {/* Membership Plans Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto mb-24 items-stretch">
        {plans.map((p, idx) => {
          const saveAmt = (p.wallet_value || 0) - p.price;
          const pctExtra = p.price > 0 ? Math.round((saveAmt / p.price) * 100) : 0;
          const isHighlighted = p.is_popular || (p.name || "").toLowerCase().includes("gold");
          const theme = getPlanTheme(p.name, isHighlighted);

          return (
            <motion.div 
              key={p.id} 
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1.1, ease: [0.25, 1, 0.5, 1] }}
              className={`relative p-4 transition-all duration-300 bg-gradient-to-b from-hpaper via-black/90 to-hpaper flex flex-col justify-between rounded-2xl min-h-[540px] ${theme.border}`}
            >
              {/* Highlight top bar for popular/gold tier */}
              {isHighlighted && (
                <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-amber-500 text-black text-[10px] font-extrabold uppercase px-6 py-1 tracking-widest rounded-full shadow-md">
                  MOST POPULAR
                </div>
              )}

              <div>
                <div className={`text-[11px] uppercase tracking-[0.25em] mb-1 ${theme.eyebrow}`}>
                  {(p.name || "").replace("Membership", "").trim().toUpperCase()}
                </div>
                
                <h3 className={`font-heading text-[22px] sm:text-2xl lg:text-[24px] xl:text-3xl mb-6 font-bold tracking-wide whitespace-nowrap ${theme.title}`}>
                  {p.name}
                </h3>

                <div className="space-y-4 mb-6">
                  <div>
                    <div className="text-white/40 text-xs mb-1 uppercase tracking-wider font-medium">You pay</div>
                    <div className={`font-heading text-4xl font-extrabold tracking-tight ${theme.price}`}>
                      {inr(p.price)}
                    </div>
                  </div>

                  {p.wallet_value > 0 && (
                    <div>
                      <div className="text-white/40 text-xs mb-1 uppercase tracking-wider font-medium">You receive in your wallet</div>
                      <div className={`font-heading text-2xl font-bold tracking-tight ${theme.wallet}`}>
                        {inr(p.wallet_value)}
                      </div>
                    </div>
                  )}

                  {saveAmt > 0 && (
                    <div className={`text-xs font-bold uppercase tracking-wider py-1.5 px-3 rounded-lg text-center ${theme.savePill}`}>
                      YOU SAVE {inr(saveAmt)} • {pctExtra}% EXTRA
                    </div>
                  )}
                </div>

                {p.description && (
                  <p className="text-white/80 text-sm leading-relaxed mb-6 font-normal">
                    {p.description}
                  </p>
                )}
                
                <ul className="space-y-3 text-sm text-white/90 mb-8 font-normal">
                  {p.benefits?.map((benefit, i) => (
                    <li key={i} className="flex items-start gap-2.5">
                      <span className={`${theme.check} text-sm shrink-0 font-bold`}>✓</span>
                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <button 
                  onClick={() => { 
                    setSelectedPlan(p); 
                    if (user) {
                      setLeadForm({ name: user.name || "", phone: user.phone || "", email: user.email || "", password: "" });
                    } else {
                      setLeadForm({ name: "", phone: "", email: "", password: "" });
                    }
                    setShowModal(true); 
                  }}
                  className={`w-full ${theme.btn}`}
                >
                  Buy {(p.name || "").replace("Membership", "").trim()}
                </button>
                {p.bottom_text && (
                  <div className="text-[10px] text-white/50 text-center mt-4 tracking-wide font-medium">
                    {p.bottom_text}
                  </div>
                )}
              </div>
            </motion.div>
          );
        })}
        {plans.length === 0 && (
          <div className="col-span-full border border-white/10 p-16 text-center bg-hpaper/20 rounded-2xl">
            <Clock className="text-hgold mb-4 mx-auto stroke-1 animate-pulse" size={48} />
            <h2 className="font-heading text-2xl text-white mb-2">Memberships Coming Soon</h2>
            <p className="text-hsecondary text-sm">We are currently detailing our spa membership tiers. Please contact our front desk directly for custom packages.</p>
            <Link to="/contact" className="gold-btn mt-6 mx-auto inline-flex">Get in Touch</Link>
          </div>
        )}
      </div>

      {/* Divider Line Above How It Works */}
      <div className="w-full h-[1px] bg-gradient-to-r from-transparent via-hgold/40 to-transparent my-16" />

      {/* How it works Section */}
      <section className="pt-2">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1.0, ease: [0.25, 1, 0.5, 1] }}
          className="text-center mb-12"
        >
          <div className="label-eyebrow mb-3 text-emerald-400 font-extrabold tracking-widest uppercase">Simple Step Process</div>
          <h2 className="text-4xl lg:text-5xl font-heading text-transparent bg-clip-text bg-gradient-to-r from-white via-amber-200 to-emerald-400">
            How it works
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {[
            {
              stepNum: "01",
              i: Sparkles,
              t: "Select Membership Tier",
              d: "Choose between Silver, Gold, Platinum or Diamond plans to maximize bonus value.",
              circle: "border border-amber-400/60 bg-amber-950/20 text-amber-400 shadow-md shadow-amber-950/20",
              titleColor: "text-amber-400",
              cardBorder: "border border-amber-400/80 hover:border-amber-300 shadow-lg shadow-amber-950/30"
            },
            {
              stepNum: "02",
              i: FileText,
              t: "Request Approval",
              d: "Select Pay at Counter or Online option to submit your membership application.",
              circle: "border border-cyan-400/60 bg-cyan-950/20 text-cyan-400 shadow-md shadow-cyan-950/20",
              titleColor: "text-cyan-400",
              cardBorder: "border border-cyan-400/80 hover:border-cyan-300 shadow-lg shadow-cyan-950/30"
            },
            {
              stepNum: "03",
              i: Wallet,
              t: "Instant Wallet Credit",
              d: "Upon admin approval, plan wallet value is instantly credited into your Hammam Wallet.",
              circle: "border border-emerald-400/60 bg-emerald-950/20 text-emerald-400 shadow-md shadow-emerald-950/20",
              titleColor: "text-emerald-400",
              cardBorder: "border border-emerald-400/80 hover:border-emerald-300 shadow-lg shadow-emerald-950/30"
            },
            {
              stepNum: "04",
              i: CheckCircle2,
              t: "Book & Deduct",
              d: "Book In-Spa or Doorstep sessions and pay directly using your Hammam Wallet balance.",
              circle: "border border-rose-400/60 bg-rose-950/20 text-rose-400 shadow-md shadow-rose-950/20",
              titleColor: "text-rose-400",
              cardBorder: "border border-rose-400/80 hover:border-rose-300 shadow-lg shadow-rose-950/30"
            },
          ].map((f, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1.0, ease: [0.25, 1, 0.5, 1] }}
              className={`text-center p-6 bg-gradient-to-b from-hpaper via-black/80 to-hpaper rounded-2xl ${f.cardBorder} transition-all duration-300 group relative`}
            >
              <div className="absolute top-4 right-4 text-[10px] font-extrabold tracking-widest text-white/30 group-hover:text-white/60 transition-colors">
                STEP {f.stepNum}
              </div>
              <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-5 transition-transform duration-500 group-hover:scale-110 ${f.circle}`}>
                <f.i size={24} strokeWidth={1.8} />
              </div>
              <div className={`font-heading text-xl mb-2 font-bold ${f.titleColor}`}>{f.t}</div>
              <div className="text-hsecondary text-sm leading-relaxed font-normal">{f.d}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Divider Line Below How It Works */}
      <div className="w-full h-[1px] bg-gradient-to-r from-transparent via-hgold/40 to-transparent my-20" />

      {/* Facilities Grid Section */}
      <section className="pt-2 pb-10">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1.0, ease: [0.25, 1, 0.5, 1] }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl lg:text-5xl font-heading text-transparent bg-clip-text bg-gradient-to-r from-amber-300 via-hgold to-emerald-400 mb-3">
            Our Elite. Discreet. Relaxing. Facilities
          </h2>
          <div className="text-xs uppercase tracking-[0.25em] text-emerald-400/90 font-bold">For The Modern Gentleman</div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {[
            { img: steamRoomImg, title: "Steam & Sauna", border: "border border-red-500 shadow-red-950/40" },
            { img: drinksImg, title: "Sunbath Area", border: "border border-emerald-400 shadow-emerald-950/40" },
            { img: newHugeJacuzziImg, title: "Huge Jacuzzi", border: "border border-amber-400 shadow-amber-950/40" },
            { img: newCommonShowerImg, title: "Common Shower", border: "border border-rose-400 shadow-rose-950/40" },
            { img: newDarkRoomImg, title: "Discreet Dark Room", border: "border border-hgold shadow-yellow-950/40" },
            { img: newDrinksBarImg, title: "Drinks & Lounge Bar", border: "border border-cyan-400 shadow-cyan-950/40" }
          ].map((item, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1.1, ease: [0.25, 1, 0.5, 1] }}
              className={`relative rounded-2xl overflow-hidden ${item.border} shadow-lg group`}
            >
              <div className="aspect-square overflow-hidden bg-black/40">
                <img
                  src={item.img}
                  alt={item.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Membership Dialog Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="bg-[#0c0c0c] border border-amber-400/80 text-white max-w-md mx-auto rounded-2xl p-6 lg:p-8 shadow-2xl max-h-[85vh] overflow-y-auto">
          <form onSubmit={handleApply} className="space-y-4 py-1">
            <DialogHeader className="border-b border-white/10 pb-4">
              <div className="flex items-center gap-1.5 text-[11px] font-extrabold uppercase tracking-widest text-emerald-400 mb-1">
                {!user ? (
                  <>
                    <UserPlus size={14} className="text-amber-400" /> Instant Account Creation & Membership
                  </>
                ) : (
                  <>
                    <UserCheck size={14} className="text-emerald-400" /> Account Connected: {user.name}
                  </>
                )}
              </div>
              <DialogTitle className="font-heading text-2xl text-transparent bg-clip-text bg-gradient-to-r from-amber-300 via-hgold to-emerald-400 font-bold">
                Apply for {selectedPlan?.name}
              </DialogTitle>
              <div className="text-xs text-amber-300 font-semibold mt-1">
                Pay: {inr(selectedPlan?.price || 0)} <span className="text-white/40">|</span> Receive: {inr(selectedPlan?.wallet_value || 0)} Wallet Balance
              </div>
            </DialogHeader>
            
            <div className="space-y-3 pt-2">
              <div>
                <label className="text-xs text-white/70 font-semibold mb-1 block">Full Name *</label>
                <input
                  type="text"
                  required
                  placeholder="Enter your full name"
                  value={leadForm.name}
                  onChange={(e) => setLeadForm({ ...leadForm, name: e.target.value })}
                  className="w-full bg-black border border-white/20 focus:border-amber-400 px-3.5 py-2.5 text-sm text-white outline-none rounded-xl"
                />
              </div>

              <div>
                <label className="text-xs text-white/70 font-semibold mb-1 block">Phone Number *</label>
                <input
                  type="tel"
                  required
                  placeholder="Mobile phone number"
                  value={leadForm.phone}
                  onChange={(e) => setLeadForm({ ...leadForm, phone: e.target.value })}
                  className="w-full bg-black border border-white/20 focus:border-amber-400 px-3.5 py-2.5 text-sm text-white outline-none rounded-xl"
                />
              </div>

              <div>
                <label className="text-xs text-white/70 font-semibold mb-1 block">
                  Email Address {!user ? "*" : "(Optional)"}
                </label>
                <input
                  type="email"
                  required={!user}
                  placeholder="name@domain.com"
                  value={leadForm.email}
                  onChange={(e) => setLeadForm({ ...leadForm, email: e.target.value })}
                  className="w-full bg-black border border-white/20 focus:border-amber-400 px-3.5 py-2.5 text-sm text-white outline-none rounded-xl"
                />
              </div>

              {!user && (
                <div>
                  <label className="text-xs text-white/70 font-semibold mb-1 block">Create Account Password *</label>
                  <div className="relative flex items-center">
                    <input
                      type={showPassword ? "text" : "password"}
                      required
                      minLength={6}
                      placeholder="Minimum 6 characters"
                      value={leadForm.password}
                      onChange={(e) => setLeadForm({ ...leadForm, password: e.target.value })}
                      className="w-full bg-black border border-white/20 focus:border-amber-400 px-3.5 py-2.5 pr-10 text-sm text-white outline-none rounded-xl"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 text-white/60 hover:text-amber-400 p-1"
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  <span className="text-[10px] text-amber-300/80 mt-1 block">
                    🔒 An account will be created automatically so you can access your Hammam Wallet & bookings.
                  </span>
                </div>
              )}

              <div>
                <label className="text-xs text-white/50 mb-1 block font-medium">Selected Tier</label>
                <input
                  type="text"
                  disabled
                  value={selectedPlan ? `${selectedPlan.name} (${inr(selectedPlan.price)} Pay -> ${inr(selectedPlan.wallet_value)} Wallet)` : ""}
                  className="w-full bg-white/5 border border-white/10 text-amber-300 font-bold px-3.5 py-2.5 text-xs outline-none rounded-xl cursor-not-allowed"
                />
              </div>

              <div>
                <label className="text-xs text-white/80 font-semibold mb-2 block">Choose Payment Mode</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="p-3 rounded-xl border-2 border-emerald-400 bg-emerald-950/40 text-white cursor-pointer relative shadow-md">
                    <div className="flex items-center justify-between text-xs font-bold mb-1">
                      <span className="flex items-center gap-1.5"><CreditCard size={14} className="text-emerald-400" /> Pay at Branch</span>
                      <CheckCircle2 size={14} className="text-emerald-400" />
                    </div>
                    <div className="text-[10px] text-white/70 leading-normal">Pay Cash / UPI / Card at spa counter upon approval.</div>
                  </div>

                  <div className="p-3 rounded-xl border border-white/15 bg-black/50 text-white/40 cursor-not-allowed relative opacity-60">
                    <div className="flex items-center justify-between text-xs font-bold mb-1">
                      <span className="flex items-center gap-1.5"><Lock size={14} className="text-amber-400" /> Pay Online</span>
                      <span className="text-[9px] bg-amber-950 border border-amber-400/40 text-amber-400 px-1 py-0.5 rounded font-extrabold">LOCKED (SOON)</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="gold-btn w-full justify-center mt-6 flex items-center gap-2 py-3.5 text-xs uppercase tracking-widest font-extrabold rounded-xl shadow-lg shadow-yellow-950/30"
            >
              {submitting ? (
                <>
                  <span className="premium-spinner border-t-transparent w-4 h-4"></span>
                  <span>{!user ? "Creating Account & Submitting..." : "Submitting Application..."}</span>
                </>
              ) : !user ? (
                "Create Account & Apply for Membership"
              ) : (
                "Submit Membership Application"
              )}
            </button>
          </form>
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
}
